import { memo, useMemo } from 'react';
import cn from 'classnames';
import { useChatListSubscribe, useChatMessagesSubscribe } from '../../realtime';
import { createChatApi } from '../../api/chatApi';
import { createChatRuntime, type GetAccessToken } from '../../api/runtime';
import { mergeThreadPresenceFromDetail } from '../../lib/mergeThreadPresenceFromDetail';
import { useChatController } from '../../model/useChatController';
import { AddParticipantsModal } from '../AddParticipantsModal/AddParticipantsModal';
import { CreateGroupChatModal } from '../CreateGroupChatModal/CreateGroupChatModal';
import { EditGroupChatModal } from '../EditGroupChatModal/EditGroupChatModal';
import { ChatConversation } from '../ChatConversation/ChatConversation';
import { ChatDetailsModal } from '../ChatDetailsModal/ChatDetailsModal';
import { ChatFilterBar } from '../ChatFilterBar/ChatFilterBar';
import { ForwardMessageModal } from '../ForwardMessageModal/ForwardMessageModal';
import { ReportChatModal } from '../ReportChatModal/ReportChatModal';
import { ReportMessageModal } from '../ReportMessageModal/ReportMessageModal';
import { CloseChatConfirmModal } from '../CloseChatConfirmModal/CloseChatConfirmModal';
import { DeleteChatConfirmModal } from '../DeleteChatConfirmModal/DeleteChatConfirmModal';
import { LeaveChatConfirmModal } from '../LeaveChatConfirmModal/LeaveChatConfirmModal';
import { RemoveParticipantConfirmModal } from '../RemoveParticipantConfirmModal/RemoveParticipantConfirmModal';
import { ReportSuccessModal } from '../ReportSuccessModal/ReportSuccessModal';
import { ChatSidebar } from '../ChatSidebar/ChatSidebar';

export type ChatScreenProps = {
  currentUserId: number;
  className?: string;
  enabled?: boolean;
  getAccessToken?: GetAccessToken;
  canCreateGroupChat?: boolean;
};

export const ChatScreen = memo(
  ({
    currentUserId,
    className,
    enabled = true,
    getAccessToken,
    canCreateGroupChat = false,
  }: ChatScreenProps) => {
    const runtime = useMemo(() => createChatRuntime(getAccessToken), [getAccessToken]);
    const api = useMemo(() => createChatApi(runtime), [runtime]);

    const {
      filter,
      setFilter,
      threadsSearch,
      setThreadsSearch,
      isThreadsLoading,
      isThreadsLoadingMore,
      threadsHasMore,
      loadMoreThreads,
      activeId,
      activeThread,
      activeGroupParticipantsCount,
      filteredThreads,
      messages,
      hasOlderMessages,
      isLoadingOlderMessages,
      loadOlderMessages,
      draft,
      draftAttachments,
      replyToMessage,
      pinnedMessages,
      composerFocusRequestId,
      chatDetail,
      ensureActiveChatDetailLoaded,
      chatDetailTarget,
      chatDetailView,
      isChatDetailLoading,
      chatDetailErrorMessage,
      chatReportTarget,
      chatReportReason,
      chatReportComment,
      isSubmittingChatReport,
      chatReportErrorMessage,
      isReportModalOpen,
      isReportSuccessModalOpen,
      isChatDetailsModalOpen,
      isChatReportModalOpen,
      isForwardModalOpen,
      forwardTargetMessage,
      forwardableThreads,
      forwardQuery,
      selectedForwardChatIds,
      isForwarding,
      forwardErrorMessage,
      reportTargetMessage,
      reportReason,
      reportComment,
      isSubmittingReport,
      reportErrorMessage,
      setDraft,
      setForwardQuery,
      setReportReason,
      setReportComment,
      setChatReportReason,
      setChatReportComment,
      send,
      attachFiles,
      removeDraftAttachment,
      retryMessage,
      startReply,
      clearReply,
      canPinMessages,
      togglePinnedMessage,
      unpinPinnedMessage,
      isPinningMessage,
      openForwardMessage,
      closeForwardModal,
      toggleForwardChat,
      submitForward,
      openReportMessage,
      closeReportModal,
      closeReportSuccessModal,
      submitReport,
      openChatDetails,
      closeChatDetails,
      openChatDetailView,
      goBackFromChatDetailView,
      closeChatReportModal,
      submitChatReport,
      selectChat,
      isCreateChatModalOpen,
      openCreateChatModal,
      closeCreateChatModal,
      availableUsersQuery,
      setAvailableUsersQuery,
      availableUsers,
      isAvailableUsersLoading,
      isCreatingPersonalChat,
      createPersonalChat,
      isCreateGroupChatModalOpen,
      openCreateGroupChatModal,
      closeCreateGroupChatModal,
      groupTitle,
      setGroupTitle,
      groupAvatarFile,
      groupAvatarPreviewUrl,
      setGroupAvatarFile,
      groupParticipantsQuery,
      setGroupParticipantsQuery,
      selectedGroupParticipantIds,
      toggleGroupParticipant,
      isCreatingGroupChat,
      createGroupErrorMessage,
      createGroupChat,
      canManageActiveGroup,
      canManageActiveGroupParticipants,
      openEditGroupChat,
      isEditGroupChatModalOpen,
      closeEditGroupChatModal,
      editGroupSnapshot,
      editGroupTitle,
      setEditGroupTitle,
      editGroupStatus,
      setEditGroupStatus,
      editGroupAvatarFile,
      editGroupAvatarPreviewUrl,
      setEditGroupAvatarFile,
      editRemovedUserIds,
      editAddedUsers,
      removeEditGroupParticipant,
      undoRemoveEditGroupParticipant,
      toggleEditGroupAddedUser,
      editParticipantsQuery,
      setEditParticipantsQuery,
      isUpdatingGroupChat,
      updateGroupErrorMessage,
      canSaveGroupEdit,
      updateGroupChat,
      activeCanLeave,
      canSendMessages,
      composerPlaceholder,
      isLeavingChat,
      isLeaveChatModalOpen,
      leaveChatModalTitle,
      leaveChatErrorMessage,
      openLeaveChatModal,
      closeLeaveChatModal,
      confirmLeaveChat,
      canDeleteActiveGroup,
      isDeletingChat,
      isDeleteChatModalOpen,
      deleteChatModalTitle,
      deleteChatErrorMessage,
      openDeleteChatModal,
      closeDeleteChatModal,
      confirmDeleteChat,
      isTogglingChatStatus,
      isCloseChatModalOpen,
      closeChatModalTitle,
      closeChatErrorMessage,
      openToggleChatStatusModal,
      closeCloseChatModal,
      confirmCloseChat,
      canToggleGroupAdmin,
      togglingAdminUserId,
      adminToggleErrorMessage,
      toggleParticipantAdmin,
      openRemoveParticipantModal,
      isRemoveParticipantModalOpen,
      removeParticipantModalName,
      removeParticipantErrorMessage,
      closeRemoveParticipantModal,
      confirmRemoveGroupParticipant,
      removingParticipantUserId,
      isTogglingArchive,
      toggleActiveChatArchive,
    } = useChatController({
      api,
      runtime,
      currentUserId,
      enabled,
      canManageGroups: canCreateGroupChat,
    });

    const chatIds = useMemo(
      () => filteredThreads.map((thread) => thread.id),
      [filteredThreads],
    );

    const conversationThread = useMemo(() => {
      if (!activeThread) {
        return null;
      }

      let thread = activeThread;

      if (chatDetail?.id === activeThread.id) {
        thread = mergeThreadPresenceFromDetail(thread, chatDetail, currentUserId);

        if (chatDetail.title && chatDetail.title !== thread.title) {
          thread = { ...thread, title: chatDetail.title };
        }
      }

      return thread;
    }, [activeThread, chatDetail, currentUserId]);

    useChatListSubscribe(chatIds, enabled, !isThreadsLoading);
    useChatMessagesSubscribe(activeId, enabled);

    return (
      <div
        className={cn(
          'flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-content1',
          className,
        )}
      >
        <ChatFilterBar filter={filter} onFilterChange={setFilter} />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
          <ChatSidebar
            threads={filteredThreads}
            activeId={activeId}
            searchQuery={threadsSearch}
            onSearchChange={setThreadsSearch}
            isLoading={isThreadsLoading}
            isLoadingMore={isThreadsLoadingMore}
            hasMore={threadsHasMore}
            onLoadMore={loadMoreThreads}
            onSelect={selectChat}
            onMessageUser={openCreateChatModal}
            onCreateGroup={openCreateGroupChatModal}
            canCreateGroup={canCreateGroupChat}
          />
          <ChatConversation
            thread={conversationThread}
            groupParticipantsCount={activeGroupParticipantsCount}
            messages={messages}
            hasOlderMessages={hasOlderMessages}
            isLoadingOlderMessages={isLoadingOlderMessages}
            onLoadOlderMessages={loadOlderMessages}
            draft={draft}
            draftAttachments={draftAttachments}
            replyToMessage={replyToMessage}
            pinnedMessages={pinnedMessages}
            composerFocusRequestId={composerFocusRequestId}
            onDraftChange={setDraft}
            onSend={send}
            onAttachFiles={attachFiles}
            onRemoveAttachment={removeDraftAttachment}
            onRetryMessage={retryMessage}
            onReplyMessage={startReply}
            onClearReply={clearReply}
            canPinMessages={canPinMessages}
            onTogglePinMessage={togglePinnedMessage}
            onUnpinPinnedMessage={unpinPinnedMessage}
            isPinningMessage={isPinningMessage}
            onForwardMessage={openForwardMessage}
            onReportMessage={openReportMessage}
            onOpenChatDetails={openChatDetails}
            canOpenSettings={canManageActiveGroup}
            onOpenSettings={openEditGroupChat}
            ensureMenuReady={ensureActiveChatDetailLoaded}
            canLeave={activeCanLeave}
            isLeavingChat={isLeavingChat}
            onLeaveChat={openLeaveChatModal}
            canSendMessages={canSendMessages}
            composerPlaceholder={composerPlaceholder}
            isArchived={activeThread?.isArchived ?? false}
            isTogglingArchive={isTogglingArchive}
            onToggleArchive={toggleActiveChatArchive}
            canToggleChatStatus={canDeleteActiveGroup}
            isTogglingChatStatus={isTogglingChatStatus}
            onToggleChatStatus={openToggleChatStatusModal}
            canDeleteChat={canDeleteActiveGroup}
            isDeletingChat={isDeletingChat}
            onDeleteChat={openDeleteChatModal}
          />
        </div>
        <AddParticipantsModal
          isOpen={isCreateChatModalOpen}
          onClose={closeCreateChatModal}
          users={availableUsers}
          search={availableUsersQuery}
          onSearchChange={setAvailableUsersQuery}
          isLoading={isAvailableUsersLoading}
          isSubmitting={isCreatingPersonalChat}
          onSubmit={createPersonalChat}
        />
        <CreateGroupChatModal
          isOpen={isCreateGroupChatModalOpen}
          onClose={closeCreateGroupChatModal}
          title={groupTitle}
          onTitleChange={setGroupTitle}
          avatarFile={groupAvatarFile}
          avatarPreviewUrl={groupAvatarPreviewUrl}
          onAvatarFileChange={setGroupAvatarFile}
          users={availableUsers}
          search={groupParticipantsQuery}
          onSearchChange={setGroupParticipantsQuery}
          selectedUserIds={selectedGroupParticipantIds}
          onToggleUser={toggleGroupParticipant}
          isLoadingUsers={isAvailableUsersLoading}
          isSubmitting={isCreatingGroupChat}
          errorMessage={createGroupErrorMessage}
          onSubmit={createGroupChat}
        />
        <ForwardMessageModal
          isOpen={isForwardModalOpen}
          message={forwardTargetMessage}
          threads={forwardableThreads}
          search={forwardQuery}
          selectedChatIds={selectedForwardChatIds}
          isSubmitting={isForwarding}
          errorMessage={forwardErrorMessage}
          onSearchChange={setForwardQuery}
          onToggleChat={toggleForwardChat}
          onClose={closeForwardModal}
          onSubmit={submitForward}
        />
        <ReportMessageModal
          isOpen={isReportModalOpen}
          message={reportTargetMessage}
          selectedReason={reportReason}
          comment={reportComment}
          isSubmitting={isSubmittingReport}
          errorMessage={reportErrorMessage}
          onReasonChange={setReportReason}
          onCommentChange={setReportComment}
          onClose={closeReportModal}
          onSubmit={submitReport}
        />
        <ReportChatModal
          isOpen={isChatReportModalOpen}
          chatTitle={chatReportTarget?.title ?? activeThread?.title ?? 'Chat'}
          selectedReason={chatReportReason}
          comment={chatReportComment}
          isSubmitting={isSubmittingChatReport}
          errorMessage={chatReportErrorMessage}
          onReasonChange={setChatReportReason}
          onCommentChange={setChatReportComment}
          onClose={closeChatReportModal}
          onSubmit={submitChatReport}
        />
        <ChatDetailsModal
          isOpen={isChatDetailsModalOpen}
          detail={chatDetail}
          titleFallback={chatDetailTarget?.title ?? activeThread?.title ?? 'Chat'}
          isLoading={isChatDetailLoading}
          errorMessage={chatDetailErrorMessage}
          view={chatDetailView}
          canManageGroup={canManageActiveGroup}
          onEditGroup={openEditGroupChat}
          canLeave={activeCanLeave}
          isLeavingChat={isLeavingChat}
          leaveErrorMessage={leaveChatErrorMessage}
          onLeaveChat={openLeaveChatModal}
          canDeleteGroup={canDeleteActiveGroup}
          isDeletingChat={isDeletingChat}
          deleteErrorMessage={deleteChatErrorMessage}
          onDeleteGroup={openDeleteChatModal}
          currentUserId={currentUserId}
          isStaffUser={canCreateGroupChat}
          canToggleAdmin={canToggleGroupAdmin}
          togglingAdminUserId={togglingAdminUserId}
          adminToggleErrorMessage={adminToggleErrorMessage}
          onToggleParticipantAdmin={toggleParticipantAdmin}
          isArchived={activeThread?.isArchived ?? false}
          isTogglingArchive={isTogglingArchive}
          onToggleArchive={toggleActiveChatArchive}
          onClose={closeChatDetails}
          onBack={goBackFromChatDetailView}
          onOpenView={openChatDetailView}
          pinnedMessages={pinnedMessages}
          canManageParticipants={canManageActiveGroupParticipants}
          onRemoveParticipant={openRemoveParticipantModal}
          removingParticipantUserId={removingParticipantUserId}
        />
        <EditGroupChatModal
          isOpen={isEditGroupChatModalOpen}
          onClose={closeEditGroupChatModal}
          title={editGroupTitle}
          onTitleChange={setEditGroupTitle}
          status={editGroupStatus}
          onStatusChange={setEditGroupStatus}
          avatarFile={editGroupAvatarFile}
          avatarPreviewUrl={editGroupAvatarPreviewUrl}
          onAvatarFileChange={setEditGroupAvatarFile}
          participants={editGroupSnapshot?.participants ?? []}
          addedUsers={editAddedUsers}
          removedUserIds={editRemovedUserIds}
          onRemoveParticipant={removeEditGroupParticipant}
          onUndoRemoveParticipant={undoRemoveEditGroupParticipant}
          pickerUsers={availableUsers}
          search={editParticipantsQuery}
          onSearchChange={setEditParticipantsQuery}
          onToggleAddUser={toggleEditGroupAddedUser}
          isLoadingUsers={isAvailableUsersLoading}
          isSubmitting={isUpdatingGroupChat}
          errorMessage={updateGroupErrorMessage}
          canSave={canSaveGroupEdit}
          onSubmit={updateGroupChat}
          canDelete={canDeleteActiveGroup}
          isDeleting={isDeletingChat}
          onDelete={openDeleteChatModal}
        />
        <LeaveChatConfirmModal
          isOpen={isLeaveChatModalOpen}
          chatTitle={leaveChatModalTitle || activeThread?.title || 'Chat'}
          isSubmitting={isLeavingChat}
          errorMessage={leaveChatErrorMessage}
          onClose={closeLeaveChatModal}
          onConfirm={confirmLeaveChat}
        />
        <DeleteChatConfirmModal
          isOpen={isDeleteChatModalOpen}
          chatTitle={deleteChatModalTitle || activeThread?.title || 'Chat'}
          isSubmitting={isDeletingChat}
          errorMessage={deleteChatErrorMessage}
          onClose={closeDeleteChatModal}
          onConfirm={confirmDeleteChat}
        />
        <CloseChatConfirmModal
          isOpen={isCloseChatModalOpen}
          chatTitle={closeChatModalTitle || activeThread?.title || 'Chat'}
          isSubmitting={isTogglingChatStatus}
          errorMessage={closeChatErrorMessage}
          onClose={closeCloseChatModal}
          onConfirm={confirmCloseChat}
        />
        <RemoveParticipantConfirmModal
          isOpen={isRemoveParticipantModalOpen}
          participantName={removeParticipantModalName}
          isSubmitting={removingParticipantUserId !== null}
          errorMessage={removeParticipantErrorMessage}
          onClose={closeRemoveParticipantModal}
          onConfirm={confirmRemoveGroupParticipant}
        />
        <ReportSuccessModal
          isOpen={isReportSuccessModalOpen}
          onClose={closeReportSuccessModal}
        />
      </div>
    );
  },
);

ChatScreen.displayName = 'ChatScreen';
